import styles from "./SurveyQuestionFlow.module.scss";

import { useMemo } from "react";

import { QuestionRenderer } from "@/modules/session-survey/components/QuestionRenderer";
import { SurveyFooter } from "@/modules/session-survey/components/SurveyFooter";
import { SurveyHeader } from "@/modules/session-survey/components/SurveyHeader";
import { useSessionSurveyContext } from "@/modules/session-survey/context/useSessionSurveyContext";
import { SurveyConfirmation } from "@/modules/session-survey/screens/SurveyConfirmation";
import { formatSurveySessionDate } from "@/modules/session-survey/utils/formatSurveySessionDate";
import { useOverflowDetection } from "@/modules/shared/hooks/useOverflowDetection";

export const SurveyQuestionFlow = () => {
  const {
    canGoBack,
    canGoForward,
    canSubmit,
    currentIndex,
    currentQuestion,
    goBack,
    goForward,
    hasCurrentAnswer,
    hasSubmitError,
    isCompleted,
    isLastStep,
    isSubmitting,
    sessionContext,
    submit,
    totalSteps,
  } = useSessionSurveyContext();
  const { ref, hasOverflow } = useOverflowDetection<HTMLDivElement>();
  const formattedSessionDate = useMemo(
    () => formatSurveySessionDate(sessionContext.sessionDate),
    [sessionContext.sessionDate],
  );

  if (isCompleted) {
    return (
      <div ref={ref} className={styles.flow}>
        <div className={styles.content}>
          <SurveyConfirmation />
        </div>
        <SurveyFooter hasOverflow={hasOverflow} />
      </div>
    );
  }

  return (
    <div ref={ref} className={styles.flow}>
      <SurveyHeader
        coachName={sessionContext.coachName}
        coachPhotoUrl={sessionContext.coachPhotoUrl ?? undefined}
        sessionDate={formattedSessionDate}
      />
      <div className={styles.content}>
        <QuestionRenderer question={currentQuestion} />
      </div>
      <SurveyFooter
        currentStep={currentIndex + 1}
        totalSteps={totalSteps}
        canGoBack={canGoBack}
        onBack={goBack}
        canGoForward={canGoForward}
        onForward={goForward}
        isForwardSelected={hasCurrentAnswer}
        isLastStep={isLastStep}
        canSubmit={canSubmit}
        onSubmit={submit}
        isSubmitting={isSubmitting}
        hasError={hasSubmitError}
        hasOverflow={hasOverflow}
      />
    </div>
  );
};
